/**
 * Typed hooks wrapping the leave contract's ts-rest react-query bindings.
 *
 * Kept separate from Leave.tsx so the page component reads as UI, not query plumbing — and so a
 * later leave screen (Approvals, Holiday Calendar) can reuse these instead of re-deriving query
 * keys. Every list is scoped to "the caller" by omitting `personId` (the API defaults to the
 * caller per leave.contract.ts), which is all this pass needs — see AppShell's VIEWER TODO for why
 * there is no real "whose data" selector yet.
 */

import { useQuery, useQueryClient, skipToken } from '@tanstack/react-query';
import type { Holiday } from '@hrms/shared';
import { tsr } from '../../lib/api.js';

/** Matches `leaveSessionSchema` in packages/shared/src/schemas/common.ts — not re-exported as a
 * standalone type from @hrms/shared, so mirrored here rather than importing the zod schema just
 * to infer one union. */
export type LeaveSession = 'full' | 'first_half' | 'second_half';

const LEAVE_KEY = 'leave';

export const leaveKeys = {
  types: () => [LEAVE_KEY, 'types'] as const,
  balances: () => [LEAVE_KEY, 'balances'] as const,
  requests: () => [LEAVE_KEY, 'requests'] as const,
  requestDetail: (leaveRequestId: string | undefined) =>
    [LEAVE_KEY, 'requests', 'detail', leaveRequestId ?? 'none'] as const,
  summary: () => [LEAVE_KEY, 'summary'] as const,
  holidays: (year: number | undefined, includeUnpublished: boolean, locationId?: string) =>
    [LEAVE_KEY, 'holidays', year ?? 'current', includeUnpublished, locationId ?? 'own'] as const,
  compOffCredits: () => [LEAVE_KEY, 'comp-off', 'credits'] as const,
  quote: (input: LeaveQuoteInput | null) => [LEAVE_KEY, 'quote', input] as const,
  approvals: (status: ApprovalStatus) => [LEAVE_KEY, 'approvals', status] as const,
  calendar: (input: LeaveCalendarInput) =>
    [
      LEAVE_KEY,
      'calendar',
      input.from,
      input.to,
      input.departmentId ?? null,
      input.locationId ?? null,
    ] as const,
  /** A REPORT's balance — the manager's approvals detail panel reads someone else's balance,
   * which `leaveKeys.balances()` (always the caller's own) can't key against without colliding. */
  balancesFor: (personId: string | undefined) =>
    [LEAVE_KEY, 'balances', 'for', personId ?? 'self'] as const,
  locations: () => [LEAVE_KEY, 'locations'] as const,
};

/**
 * `includeInactive` is only ever honoured by the API for a caller holding `leave.policy:manage`
 * (see listLeaveTypesQuerySchema's own doc comment) — passing it here for an employee is harmless,
 * the server just ignores it, but callers should still not bother. Same convention as
 * `useHolidays`'s `includeUnpublished`.
 */
export function useLeaveTypes(options?: { includeInactive?: boolean }) {
  const { includeInactive = false } = options ?? {};
  return tsr.leave.listTypes.useQuery({
    queryKey: [...leaveKeys.types(), includeInactive] as const,
    queryData: { query: includeInactive ? { includeInactive: 'true' as const } : {} },
  });
}

/** The org's legal entities — used by the Leave Policies page to resolve a leave type's
 * `legalEntityId` to a real entity name for its "applies to X" line, rather than guessing or
 * hardcoding one. Gated on `legal_entity:read`, which every role holds (see
 * packages/shared/src/contracts/lookup.contract.ts). Unpaginated: bounded reference data, not a
 * directory that grows with headcount. */
export function useLegalEntities() {
  return tsr.lookup.listLegalEntities.useQuery({
    queryKey: [LEAVE_KEY, 'legal-entities'] as const,
  });
}

export function useLeaveBalances() {
  return tsr.leave.listBalances.useQuery({
    queryKey: leaveKeys.balances(),
    queryData: { query: {} },
  });
}

export function useLeaveRequests() {
  return tsr.leave.listRequests.useQuery({
    queryKey: leaveKeys.requests(),
    queryData: { query: {} },
  });
}

/** One leave request's full detail — the caller's own, or one they hold approval visibility over;
 * a 404 here means "not yours and you can't see it", not a bug. Disabled until an id is known. */
export function useLeaveRequest(leaveRequestId: string | undefined) {
  return tsr.leave.getRequest.useQuery({
    queryKey: leaveKeys.requestDetail(leaveRequestId),
    queryData: { params: { leaveRequestId: leaveRequestId! } },
    enabled: Boolean(leaveRequestId),
  });
}

export function useLeaveSummary() {
  return tsr.leave.getSummary.useQuery({
    queryKey: leaveKeys.summary(),
    queryData: { query: {} },
  });
}

/**
 * No `locationId` param by default: the API resolves the caller's own location server-side when
 * it's omitted (see leave.ts's `listHolidays` — `resolvePersonScope` fills it in). The Holiday
 * Calendar page passes one explicitly once the caller picks a location filter.
 *
 * `includeUnpublished` is only ever honoured by the API for a caller holding
 * `holiday_calendar:manage` (see listHolidaysQuerySchema's own doc comment) — passing it here for
 * an employee is harmless, the server just ignores it, but callers should still not bother.
 */
export function useHolidays(options?: {
  year?: number;
  locationId?: string;
  includeUnpublished?: boolean;
}) {
  const { year, locationId, includeUnpublished = false } = options ?? {};
  return tsr.leave.listHolidays.useQuery({
    queryKey: leaveKeys.holidays(year, includeUnpublished, locationId),
    queryData: {
      query: {
        limit: 100, // the contract's max — see common.ts's shared pagination schema
        ...(year ? { year } : {}),
        ...(locationId ? { locationId } : {}),
        ...(includeUnpublished ? { includeUnpublished: 'true' as const } : {}),
      },
    },
  });
}

/**
 * Every holiday of the year for every location, drafts included — the Holiday Management console.
 * Follows `nextCursor` to the end: one holiday added for many locations is one row per location,
 * so a single page of 100 silently dropped rows in a company with many locations. Only a caller
 * holding `holiday_calendar:manage` is ever given drafts or other locations' rows (the API
 * enforces that), so this is for that console alone.
 */
export function useAllHolidays(options?: { year?: number }) {
  const { year } = options ?? {};
  return useQuery({
    queryKey: leaveKeys.holidays(year, true, 'all'),
    queryFn: async () => {
      const items: Holiday[] = [];
      let cursor: string | undefined;
      for (let guard = 0; guard < 100; guard++) {
        const response = await tsr.leave.listHolidays.query({
          query: {
            limit: 100,
            ...(year ? { year } : {}),
            includeUnpublished: 'true' as const,
            ...(cursor ? { cursor } : {}),
          },
        });
        if (response.status !== 200) throw new Error('Could not load holidays');
        items.push(...response.body.items);
        if (!response.body.nextCursor) break;
        cursor = response.body.nextCursor;
      }
      return items;
    },
  });
}

/** The org's work locations — the filter/picker on both Holiday Calendar layouts. Reuses the real
 * `lookup.listLocations` endpoint (added alongside the Organisation module) rather than the
 * leave-scoped duplicate this hook used to wrap — same consolidation `useLegalEntities` in this
 * file already does for `lookup.listLegalEntities`. Unpaginated: bounded reference data. Response
 * is a plain array (`z.array(locationSchema)`), not `paginatedSchema` — no `.items` to unwrap. */
export function useLocations() {
  return tsr.lookup.listLocations.useQuery({
    queryKey: leaveKeys.locations(),
  });
}

export type ApprovalStatus = 'pending' | 'decided';

/** Leave requests currently or previously assigned to the caller for decision — the manager's
 * approvals inbox. `limit: 100` (the contract's max) because this list has no separate count
 * endpoint; the KPI strip and tab counts below are derived from however many of these come back,
 * same honesty tradeoff as `pendingApprovalDays` elsewhere on this page. */
export function useLeaveApprovals(status: ApprovalStatus) {
  return tsr.leave.listApprovals.useQuery({
    queryKey: leaveKeys.approvals(status),
    queryData: { query: { status, limit: 100 } },
  });
}

/** A report's current balances, for the manager's Balance Impact Preview — `leave.balance:read`
 * covers reading a visible report's balance, not just the caller's own (see leaveBalanceQuerySchema
 * in packages/shared/src/contracts/leave.contract.ts). Disabled until a `personId` is picked. */
export function useLeaveBalancesFor(personId: string | undefined) {
  return tsr.leave.listBalances.useQuery({
    queryKey: leaveKeys.balancesFor(personId),
    queryData: { query: personId ? { personId } : {} },
    enabled: Boolean(personId),
  });
}

/**
 * Colleague search for the "Filing For" and "Task Delegate" pickers in Request Leave — uses
 * `lookup.searchColleagues`, NOT `employees.list`. The Employee Directory endpoint scopes results
 * row-by-row through `can(actor, 'employee:read', target)`, and a plain employee's only grant for
 * that permission is `self` scope — every row except their own vanishes, which made this picker
 * useless for anyone but a manager/HR searching within their own visibility. `searchColleagues` is
 * gated on `canAnywhere(actor, 'employee:read')` instead (any role, any scope — "is this a real
 * employee of the org", not "can you see this specific person's record") and returns a minimal,
 * safe-to-expose-org-wide shape (name/code/department, no email/phone/PII). Disabled until at
 * least one character is typed, same debounce-free convention as before (fires per keystroke;
 * react-query's own caching absorbs the repeats).
 */
export function useEmployeeSearch(q: string) {
  const query = q.trim();
  return tsr.lookup.searchColleagues.useQuery({
    queryKey: [LEAVE_KEY, 'colleague-search', query] as const,
    queryData: { query: { q: query, limit: 8 } },
    enabled: query.length > 0,
  });
}

export interface LeaveCalendarInput {
  from: string;
  to: string;
  /** HR/org-scope only — the API ignores these for a manager-tier or plain-employee caller, whose
   * visible team is already resolved from their own position in the org chart. Harmless to pass
   * regardless; same "the server decides, the client doesn't need to know" convention as
   * `useHolidays`'s `includeUnpublished`. */
  departmentId?: string;
  locationId?: string;
}

/**
 * The Team Calendar — "who's out today/this week". `limit: 100` (the contract's max) because
 * `getLeaveCalendarQuerySchema` caps the window at 93 days and this is a UI surface, not a
 * reporting export, same tradeoff `useLeaveApprovals` already makes for the approvals inbox.
 */
export function useLeaveCalendar(input: LeaveCalendarInput) {
  return tsr.leave.getCalendar.useQuery({
    queryKey: leaveKeys.calendar(input),
    queryData: {
      query: {
        from: input.from,
        to: input.to,
        limit: 100,
        ...(input.departmentId ? { departmentId: input.departmentId } : {}),
        ...(input.locationId ? { locationId: input.locationId } : {}),
      },
    },
  });
}

export function useCompOffCredits() {
  return tsr.leave.listCompOffCredits.useQuery({
    queryKey: leaveKeys.compOffCredits(),
    queryData: { query: {} },
  });
}

export interface LeaveQuoteInput {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  startSession: LeaveSession;
  endSession: LeaveSession;
  /** Required, and only meaningful, when the leave type is hour-unit — see `leaveTypeUnitSchema`. */
  hours?: string;
  /** On-behalf-of filing — quote against the target person's own balance, not the caller's. */
  personId?: string;
}

/**
 * The live quote in the "Request Leave" form. Driven by `useQuery`, not a mutation — `quoteRequest`
 * is a GET in the contract (it has no side effect to trigger), and keying the query on the form
 * fields themselves means React Query already does the "only refetch when something that matters
 * changed" debouncing this needs, with no extra state to manage.
 */
export function useLeaveQuote(input: LeaveQuoteInput | null) {
  return tsr.leave.quoteRequest.useQuery({
    queryKey: leaveKeys.quote(input),
    queryData: input ? { query: input } : skipToken,
  });
}

/** Every write here invalidates the same four lists: a new/cancelled request changes what balance
 * is left, what's pending, the summary roll-up, and (for comp-off) the credits it consumed. */
function invalidateLeaveQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: leaveKeys.balances() });
  queryClient.invalidateQueries({ queryKey: leaveKeys.requests() });
  queryClient.invalidateQueries({ queryKey: leaveKeys.summary() });
  queryClient.invalidateQueries({ queryKey: leaveKeys.compOffCredits() });
}

export function useCreateLeaveRequest() {
  const queryClient = useQueryClient();
  return tsr.leave.createRequest.useMutation({
    onSuccess: () => invalidateLeaveQueries(queryClient),
  });
}

export function useCancelLeaveRequest() {
  const queryClient = useQueryClient();
  return tsr.leave.cancelRequest.useMutation({
    onSuccess: () => invalidateLeaveQueries(queryClient),
  });
}

/** A decision (approve/reject) changes both the approvals inbox (pending → decided) and, for
 * whatever overlaps this browser's own cache (a manager who is also someone's report), the same
 * four lists a create/cancel touches. Matches `[LEAVE_KEY, 'approvals']` — no `status` — so a
 * single invalidation call catches both the pending and decided queries at once. */
function invalidateApprovalQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [LEAVE_KEY, 'approvals'] });
  invalidateLeaveQueries(queryClient);
}

export function useApproveLeaveRequest() {
  const queryClient = useQueryClient();
  return tsr.leave.approveRequest.useMutation({
    onSuccess: () => invalidateApprovalQueries(queryClient),
  });
}

export function useRejectLeaveRequest() {
  const queryClient = useQueryClient();
  return tsr.leave.rejectRequest.useMutation({
    onSuccess: () => invalidateApprovalQueries(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Holiday Calendar — create/update/delete (holiday_calendar:manage only; the API enforces this
// server-side regardless of what the client attempts)
// ---------------------------------------------------------------------------

/** Matches every `leaveKeys.holidays(...)` entry regardless of year/includeUnpublished — a create/
 * update/delete can change what any of those cached queries would return. */
function invalidateHolidayQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [LEAVE_KEY, 'holidays'] });
}

export function useCreateHoliday() {
  const queryClient = useQueryClient();
  return tsr.leave.createHoliday.useMutation({
    onSuccess: () => invalidateHolidayQueries(queryClient),
  });
}

export function useUpdateHoliday() {
  const queryClient = useQueryClient();
  return tsr.leave.updateHoliday.useMutation({
    onSuccess: () => invalidateHolidayQueries(queryClient),
  });
}

export function useDeleteHoliday() {
  const queryClient = useQueryClient();
  return tsr.leave.deleteHoliday.useMutation({
    onSuccess: () => invalidateHolidayQueries(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Leave Policies — create/update (leave.policy:manage only; the API enforces this server-side
// regardless of what the client attempts). Deliberately no delete — deactivate via `isActive: false`.
// ---------------------------------------------------------------------------

/** Matches every `leaveKeys.types()` entry regardless of `includeInactive` — a create/update can
 * change what either cached query would return. */
function invalidateLeaveTypeQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: leaveKeys.types() });
}

export function useCreateLeaveType() {
  const queryClient = useQueryClient();
  return tsr.leave.createLeaveType.useMutation({
    onSuccess: () => invalidateLeaveTypeQueries(queryClient),
  });
}

export function useUpdateLeaveType() {
  const queryClient = useQueryClient();
  return tsr.leave.updateLeaveType.useMutation({
    onSuccess: () => invalidateLeaveTypeQueries(queryClient),
  });
}

/** A fresh key per mutation attempt — see createRequest/cancelRequest's `idempotency()` middleware
 * (required by default, CLAUDE.md invariant 10). Generated client-side so a retried submit (double
 * click, flaky network) reuses the SAME key and replays the original outcome instead of filing the
 * request twice; a fresh key here means a fresh key per user-initiated attempt, which is correct —
 * the browser's own fetch retry (not user-initiated) is what actually needs replay to be safe, and
 * this key stays fixed for the lifetime of that one fetch call. */
export function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `leave-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
